// @ts-expect-error - TS2614 - Module '"src/api-parsers/index"' has no exported member 'Workflow'. Did you mean to use 'import Workflow from "src/api-parsers/index"' instead?
import type {Workflow} from 'src/api-parsers/index';
import type {
  Dispatch,
  GetState,
  ThunkAction,
  ThunkSyncAction,
} from 'src/reducers/index';
import type {BusinessGoals} from 'src/types/workflow';
import type {BackgroundTaskStatusResponse} from 'src/action-creators/background-task';
import type {BackgroundTaskStatus} from 'src/reducers/background-task';
import type {EntityType} from 'src/types/ats-entities';
import IndexStore from 'src/stores/index';

import omit from 'lodash/omit';
import sculpt from 'sculpt';

import {thunkify as flow} from 'src/utils/thunks';
import {camel} from 'src/utils';
import {key, cached, fetching} from 'src/utils/redux';
import * as reduxApi from 'src/utils/redux-api';
import logger from 'src/utils/logger';

import {
  getWorkflowActivationKey,
  getWorkflowFilterKey,
} from 'src/utils/background-task';
import {
  getBackgroundTaskStatus,
  receiveBackgroundTaskStatus,
} from 'src/action-creators/background-task';
import parsers from 'src/api-parsers/index';


export const RECEIVE_ALL = 'workflow/receiveAll';
export const UPDATE = 'workflow/update';
export const BULK_UPDATE_WORKFLOWS = 'workflow/bulkUpdate';
export const DELETE_WORKFLOW = 'workflow/delete';
export const RECEIVE_ENTITY_TYPES = 'workflow/receiveEntityTypes';
export const RECEIVE_BUSINESS_GOALS = 'workflow/receiveBusinessGoals';
export const RECEIVE_RECIPIENT_TYPES = 'workflow/receiveRecipientTypes';
export const RECEIVE_WORKFLOW = 'workflow/recieveWorkflow';

export type ReceiveWorkflowsAction = {
  type: 'workflow/receiveAll';
  payload: Workflow[];
};

export type ReceiveWorkflowAction = {
  type: 'workflow/recieveWorkflow';
  payload: Workflow[];
};

export type UpdateWorkflowAction = {
  type: 'workflow/update';
  payload: {
    workflowId: string;
    update: Partial<Workflow>;
  };
};
export type BulkUpdateWorkflowAction = {
  type: 'workflow/bulkUpdate';
  payload: {
    [key: string]: Partial<Workflow>;
  };
};

export type DeleteWorkflowAction = {
  type: 'workflow/delete';
  payload: Workflow;
};

export type ReceiveEntityTypesAction = {
  type: 'workflow/receiveEntityTypes';
  payload: EntityType[];
};

export type ReceiveBusinessGoalsAction = {
  type: 'workflow/receiveBusinessGoals';
  payload: BusinessGoals[];
};

export type ReceiveRecipientTypesAction = {
  type: 'workflow/receiveRecipientTypes';
  payload: {
    [workflowId: string]: string[];
  };
};

export type WorkflowAction =
  | ReceiveWorkflowsAction
  | UpdateWorkflowAction
  | BulkUpdateWorkflowAction
  | DeleteWorkflowAction
  | ReceiveEntityTypesAction
  | ReceiveRecipientTypesAction
  | ReceiveBusinessGoalsAction
  | ReceiveWorkflowAction;

export const receiveWorkflows = (
  payload: Workflow[],
): ReceiveWorkflowsAction => ({
  type: RECEIVE_ALL,
  // dupe of branchedEventIds omission in getWorkflows() in src/actions/workflow.js
  payload: payload.map((workflow) => omit(workflow, ['branchedEventIds'])),
});

export const receiveWorkflow = (payload: Workflow): ReceiveWorkflowAction => ({
  type: RECEIVE_WORKFLOW,
  payload: [payload],
});

export const updateWorkflow = (
  workflowId: string,
  update: Partial<Workflow>,
): UpdateWorkflowAction => ({
  type: UPDATE,
  payload: {
    workflowId,
    update,
  },
});
export const bulkUpdateWorkflow = (payload: {
  [key: string]: Partial<Workflow>;
}): BulkUpdateWorkflowAction => ({
  type: BULK_UPDATE_WORKFLOWS,
  payload,
});

export const deleteWorkflow = (payload: Workflow): DeleteWorkflowAction => ({
  type: DELETE_WORKFLOW,
  payload,
});

export const receiveEntityTypes = (
  payload: EntityType[],
): ReceiveEntityTypesAction => ({
  type: RECEIVE_ENTITY_TYPES,
  payload,
});

export const receiveBusinessGoals = (
  payload: BusinessGoals[],
): ReceiveBusinessGoalsAction => ({
  type: RECEIVE_BUSINESS_GOALS,
  payload,
});

export const receiveRecipientTypes = (
  workflowId: string,
  recipientTypes: string[],
): ReceiveRecipientTypesAction => ({
  type: RECEIVE_RECIPIENT_TYPES,
  payload: {[workflowId]: recipientTypes},
});

// TODO(marcos): this is currently unused
export const getAllWorkflows: () => ThunkAction<unknown> = flow(
  key(() => 'workflow/getAll'),
  cached((json) => receiveWorkflows(camel(json))),
  fetching(),
)(() => reduxApi.get('workflows'));

export const receiveWorkflowActivationStatus =
  (
    workflowId: string,
    backgroundActivationStatus: BackgroundTaskStatusResponse,
  ): ThunkSyncAction =>
  (dispatch: Dispatch) => {
    const key = getWorkflowActivationKey(workflowId);
    const statusUrl = `workflows/${workflowId}/activate/status`;
    return dispatch(
      receiveBackgroundTaskStatus({
        key,
        statusUrl,
        ...backgroundActivationStatus,
      }),
    );
  };

export const receiveWorkflowFilterStatus =
  (
    workflowId: string,
    backgroundFilterStatus: BackgroundTaskStatusResponse,
  ): ThunkSyncAction =>
  (dispatch: Dispatch) => {
    const key = getWorkflowFilterKey(workflowId);
    const statusUrl = `workflows/${workflowId}/audience-filter/status`;
    return dispatch(
      receiveBackgroundTaskStatus({
        key,
        statusUrl,
        ...camel(backgroundFilterStatus),
      }),
    );
  };

export const getWorkflowActivationStatus =
  (
    store: IndexStore,
    workflowId: string,
  ): ThunkAction<BackgroundTaskStatus | null | undefined> =>
  async (
    dispatch: Dispatch,
    // @ts-expect-error - TS7030 - Not all code paths return a value.
  ): Promise<BackgroundTaskStatus | null | undefined> => {
    try {
      const activationKey = getWorkflowActivationKey(workflowId);
      const {
        payload: {status},
      } = await dispatch(
        getBackgroundTaskStatus(
          activationKey,
          `workflows/${workflowId}/activate/status`,
        ),
      );

      const update = (state: boolean) => {
        const workflow = store.workflows.get(workflowId);
        store.workflows.update(
          //@ts-expect-error
          sculpt(workflow, {
            active: {
              $set: state,
            },
          }),
        );
        dispatch(updateWorkflow(workflowId, {active: state}));
      };

      if (status === 'inactive') {
        update(false);
      } else if (status === 'success') {
        update(true);
      }

      return status;
    } catch (error: any) {
      logger.error(error);
    }
  };

export const getWorkflowFilterStatus =
  (workflowId: string): ThunkAction<BackgroundTaskStatus | null | undefined> =>
  async (
    dispatch: Dispatch,
    // @ts-expect-error - TS7030 - Not all code paths return a value.
  ): Promise<BackgroundTaskStatus | null | undefined> => {
    try {
      const filterKey = getWorkflowFilterKey(workflowId);
      const {
        payload: {status},
      } = await dispatch(
        getBackgroundTaskStatus(
          filterKey,
          `workflows/${workflowId}/audience-filter/status`,
        ),
      );
      return status;
    } catch (error: any) {
      logger.error(error);
    }
  };

// TODO(marcos) DUPE: decorator for multientity-config?
// @ts-expect-error - TS2322 - Type '() => (dispatch: Dispatch, getState: GetState) => Promise<unknown>' is not assignable to type '() => ThunkAction<EntityType[]>'.
export const getAllowedEntityTypes: () => ThunkAction<EntityType[]> = flow(
  key(() => 'workflow/allowedEntityTypes'),
  cached((json) => receiveEntityTypes(camel(json)), {ttl: 60 * 60 * 1000}),
  // 1hr, these are tied to ats and are unlikely to change ever during a session
  fetching(),
)(() => async (dispatch: Dispatch, getState: GetState) => {
  try {
    return await dispatch(reduxApi.get('workflows/allowed-entity-types'));
  } catch (error: any) {
    if (error?.status === 403) {
      logger.error(error);
      return [];
    }
    throw error;
  }
});

// @ts-expect-error - TS2322 - Type '() => (dispatch: Dispatch, getState: GetState) => Promise<unknown>' is not assignable to type '() => ThunkAction<BusinessGoals>'.
export const getBusinessGoals: () => ThunkAction<BusinessGoals> = flow(
  key(() => 'workflow/getBusinessGoals'),
  cached((json) => receiveBusinessGoals(camel(json)), {ttl: 60 * 60 * 1000}),
  // 1hr, these are unlikely to change ever during a session
  fetching(),
)(
  () => (dispatch: Dispatch, getState: GetState) =>
    dispatch(reduxApi.get('journeys/business-goal')),
);

// TODO(marcos) DUPE: decorator for multientity-config?
// @ts-expect-error - TS2322 - Type '(workflowId: string) => (dispatch: Dispatch, getState: GetState) => Promise<unknown>' is not assignable to type '(workflowId: string) => ThunkAction<EntityType>'.
export const getEventRecipientTypes: (
  workflowId: string,
) => ThunkAction<EntityType> = flow(
  key((workflowId: string) => `allowed-recipient-types/${workflowId}`),
  cached(
    (recipientTypes, workflowId) =>
      receiveRecipientTypes(workflowId, camel(recipientTypes)),
    {ttl: 60 * 60 * 1000},
  ), // 1hr, these are tied to workflow and can't be changed easily
  fetching(),
)(
  (workflowId: string) => (dispatch: Dispatch, getState: GetState) =>
    dispatch(
      reduxApi.get(`workflows/${workflowId}/events/allowed-recipient-types`),
    ),
);

// @ts-expect-error - TS2322 - Type '(workflowId: string) => (dispatch: Dispatch, getState: GetState) => Promise<unknown>' is not assignable to type '(workflowId: string) => ThunkAction<EntityType>'.
export const getWorkflowById: (workflowId: string) => ThunkAction<EntityType> =
  flow(
    key((workflowId) => `getWorkflow ${workflowId})`),
    cached((response) => receiveWorkflow(parsers.parse.workflow(response)), {
      ttl: 60 * 60 * 1000,
    }),
    fetching(),
  )(
    (workflowId: string) => (dispatch: Dispatch, getState: GetState) =>
      dispatch(reduxApi.get(`workflows/${workflowId}`)),
  );
